import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { InstructionsModal } from './InstructionsModal';
import { getInstructionImages } from './instructionImages';

/**
 * OnboardingModal - Shows a guided onboarding flow for first-time users of external services.
 *
 * Phase 1: Checklist - Kid must confirm 3 things before proceeding
 * Phase 2: Instructions - Shows instruction slides with navigation
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onHide - Callback to close the modal
 * @param {Function} props.onComplete - Callback when onboarding is complete (opens credentials modal)
 * @param {string} props.serviceName - Display name of the service (e.g., "Duolingo")
 * @param {string} props.serviceUsername - The account username
 * @param {string} props.activityType - Type of activity (e.g., "codecombat", "duolingo", "khan_academy")
 * @param {string} props.activitySubType - Sub-type of activity (e.g., "music" for Duolingo Music)
 * @param {Object} props.instructionTextData - Data for instruction text (xp, mastery, levels)
 * @param {Array<string>} props.courses - Array of course IDs for the activity
 */
export const OnboardingModal = ({
  show,
  onHide,
  onComplete,
  serviceName,
  serviceUsername,
  activityType,
  activitySubType = null,
  instructionTextData = {},
  courses = []
}) => {
  const { t } = useTranslation();
  const [showInstructions, setShowInstructions] = useState(false);
  const [checkboxes, setCheckboxes] = useState({
    login: false,
    requirement: false,
    comeback: false
  });

  // Reset state when modal is closed
  const handleClose = useCallback(() => {
    setShowInstructions(false);
    setCheckboxes({ login: false, requirement: false, comeback: false });
    onHide();
  }, [onHide]);

  // Handle checkbox toggle
  const toggleCheckbox = useCallback((key) => {
    setCheckboxes(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Check if all checkboxes are checked
  const allChecked = checkboxes.login && checkboxes.requirement && checkboxes.comeback;

  // Get instruction images from shared config
  const instructionImages = useMemo(() => {
    return getInstructionImages(activityType, courses, activitySubType);
  }, [activityType, courses, activitySubType]);

  // Get requirement text using generic translation with action/achievement/service
  const requirementText = useMemo(() => {
    let action, achievement, service;

    // Determine service name based on sub-type or type
    if (activitySubType === 'abc') {
      service = 'Duolingo ABC';
    } else if (activitySubType === 'music') {
      service = 'Duolingo Music';
    } else if (activitySubType === 'chess' || activitySubType === 'elochess') {
      service = 'Duolingo Chess';
    } else if (activitySubType === 'math') {
      service = 'Duolingo Math';
    } else {
      service = serviceName;
    }

    // Determine action and achievement based on activity type
    if (activityType === 'codecombat') {
      action = t('children.activity_sessions.onboarding.action_complete');
      const levels = instructionTextData.levels || 3;
      achievement = t('children.activity_sessions.onboarding.achievement_levels', { levels });
    } else if (activityType === 'code_combat_ai') {
      action = t('children.activity_sessions.onboarding.action_earn');
      const stars = instructionTextData.stars || 1;
      achievement = t('children.activity_sessions.onboarding.achievement_stars', { stars });
    } else if (activityType === 'khan_academy') {
      action = t('children.activity_sessions.onboarding.action_earn');
      const mastery = instructionTextData.mastery || 50;
      achievement = t('children.activity_sessions.onboarding.achievement_mastery', { mastery });
    } else if (activityType === 'art_workout') {
      action = t('children.activity_sessions.onboarding.action_complete');
      const lessons = instructionTextData.lessons || 1;
      achievement = t('children.activity_sessions.onboarding.achievement_lessons', { lessons });
    } else if (activitySubType === 'abc') {
      // Duolingo ABC uses lessons instead of XP
      action = t('children.activity_sessions.onboarding.action_complete');
      const lessons = instructionTextData.lessons || 1;
      achievement = t('children.activity_sessions.onboarding.achievement_lessons', { lessons });
    } else if (activitySubType === 'elochess') {
      // Duolingo ELO Chess uses ELO rating instead of XP
      action = t('children.activity_sessions.onboarding.action_gain');
      const elo = instructionTextData.elo || 1;
      achievement = t('children.activity_sessions.onboarding.achievement_elo', { elo });
    } else {
      // Duolingo, Brilliant, and sub-types all use XP
      action = t('children.activity_sessions.onboarding.action_earn');
      const xp = instructionTextData.xp || 100;
      achievement = t('children.activity_sessions.onboarding.achievement_xp', { xp });
    }

    return t('children.activity_sessions.onboarding.requirement', { action, achievement, service });
  }, [activityType, activitySubType, instructionTextData, serviceName, t]);

  // Handle "View Instruction" button click
  const handleViewInstruction = useCallback(() => {
    setShowInstructions(true);
  }, []);

  // Handle instructions complete - close and trigger onComplete
  const handleInstructionsComplete = useCallback(() => {
    handleClose();
    onComplete();
  }, [handleClose, onComplete]);

  // Get instructions title - use generic translation with service name
  const instructionsTitle = useMemo(() => {
    return t('children.activity_sessions.instructions_title', { service: serviceName });
  }, [serviceName, t]);

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        data-testid="onboarding-modal"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            {/* Checklist Phase */}
            <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">
                  {t('children.activity_sessions.onboarding.title', { service: serviceName })}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                  aria-label={t('common.close')}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-secondary mb-4">
                  {t('children.activity_sessions.onboarding.description')}
                </p>

                {/* Checkbox 1: Login with username */}
                <div className="form-check mb-4 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="onboarding-login"
                    checked={checkboxes.login}
                    onChange={() => toggleCheckbox('login')}
                    data-testid="onboarding-checkbox-login"
                    style={{
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: '3px solid #0d6efd',
                      borderRadius: '6px'
                    }}
                  />
                  <label
                    className="form-check-label ms-3"
                    htmlFor="onboarding-login"
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      lineHeight: '1.4'
                    }}
                  >
                    {t('children.activity_sessions.onboarding.checkbox_login', {
                      service: serviceName,
                      account: serviceUsername
                    })}
                  </label>
                </div>

                {/* Checkbox 2: Requirement (XP/levels/mastery) */}
                <div className="form-check mb-4 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="onboarding-requirement"
                    checked={checkboxes.requirement}
                    onChange={() => toggleCheckbox('requirement')}
                    data-testid="onboarding-checkbox-requirement"
                    style={{
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: '3px solid #0d6efd',
                      borderRadius: '6px'
                    }}
                  />
                  <label
                    className="form-check-label ms-3"
                    htmlFor="onboarding-requirement"
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      lineHeight: '1.4'
                    }}
                  >
                    {requirementText}
                  </label>
                </div>

                {/* Checkbox 3: Come back to confirm */}
                <div className="form-check mb-4 d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="onboarding-comeback"
                    checked={checkboxes.comeback}
                    onChange={() => toggleCheckbox('comeback')}
                    data-testid="onboarding-checkbox-comeback"
                    style={{
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: '3px solid #0d6efd',
                      borderRadius: '6px'
                    }}
                  />
                  <label
                    className="form-check-label ms-3"
                    htmlFor="onboarding-comeback"
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      lineHeight: '1.4'
                    }}
                  >
                    {t('children.activity_sessions.onboarding.checkbox_comeback')}
                  </label>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleViewInstruction}
                  disabled={!allChecked}
                  data-testid="onboarding-view-instruction-button"
                >
                  {t('children.activity_sessions.onboarding.view_instruction')}
                </button>
              </div>
          </div>
        </div>
      </div>

      {/* Instructions Modal (onboarding mode) */}
      <InstructionsModal
        show={showInstructions}
        onHide={() => setShowInstructions(false)}
        images={instructionImages}
        title={instructionsTitle}
        testId="onboarding-instructions-modal"
        mode="onboarding"
        onComplete={handleInstructionsComplete}
      />
    </>
  );
};
